import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { PageTransition } from "./PageTransition";
import { BlogRedirect } from "./BlogRedirect";
import { lazy, Suspense } from "react";
import { cityServiceCombos } from "@/data/cityServiceData";

// Eagerly load the homepage for instant first paint
import Index from "@/pages/Index";

// Lazy-load all other pages for code splitting
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const CommercialServicesPage = lazy(() => import("@/pages/CommercialServicesPage"));
const ServiceDetailPage = lazy(() => import("@/pages/ServiceDetailPage"));
const CommercialServiceDetailPage = lazy(() => import("@/pages/CommercialServiceDetailPage"));
const TheZonePage = lazy(() => import("@/pages/TheZonePage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogArchivePage = lazy(() => import("@/pages/BlogArchivePage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));

const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ServiceAreasPage = lazy(() => import("@/pages/ServiceAreasPage"));
const GilbertRestorationPage = lazy(() => import("@/pages/GilbertRestorationPage"));
const TestimonialsPage = lazy(() => import("@/pages/TestimonialsPage"));
const CaseStudiesPage = lazy(() => import("@/pages/CaseStudiesPage"));
const CaseStudyDetailPage = lazy(() => import("@/pages/CaseStudyDetailPage"));

const NotFound = lazy(() => import("@/pages/NotFound"));
const GroutColorSealingPage = lazy(() => import("@/pages/GroutColorSealingPage"));
const GroutColorChartPage = lazy(() => import("@/pages/GroutColorChartPage"));
const HardWaterStainRemovalPage = lazy(() => import("@/pages/HardWaterStainRemovalPage"));
const PromotionsPage = lazy(() => import("@/pages/PromotionsPage"));
const ShowerRestorationPage = lazy(() => import("@/pages/ShowerRestorationPage"));
const NaturalStoneShowerPage = lazy(() => import("@/pages/NaturalStoneShowerPage"));
const TileStoneRepairPage = lazy(() => import("@/pages/TileStoneRepairPage"));
const SEOServicePage = lazy(() => import("@/pages/SEOServicePage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const WarrantyPage = lazy(() => import("@/pages/WarrantyPage"));
const CityServicePage = lazy(() => import("@/pages/CityServicePage"));
const CityLandingPage = lazy(() => import("@/pages/CityLandingPage"));
const NeighborhoodPreservationPage = lazy(() => import("@/pages/NeighborhoodPreservationPage"));
const TravertineGilbertPage = lazy(() => import("@/pages/TravertineGilbertPage"));
const GroutColorSealingGilbertAzPage = lazy(() => import("@/pages/GroutColorSealingGilbertAzPage"));
const GroutColorSealingChandlerAzPage = lazy(() => import("@/pages/GroutColorSealingChandlerAzPage"));
const GroutColorSealingMesaAzPage = lazy(() => import("@/pages/GroutColorSealingMesaAzPage"));
const GroutColorSealingParadiseValleyAzPage = lazy(() => import("@/pages/GroutColorSealingParadiseValleyAzPage"));
const TileGroutCleaningChandlerAzPage = lazy(() => import("@/pages/TileGroutCleaningChandlerAzPage"));
const TileGroutCleaningGilbertAzPage = lazy(() => import("@/pages/TileGroutCleaningGilbertAzPage"));
const TileGroutCleaningMesaAzPage = lazy(() => import("@/pages/TileGroutCleaningMesaAzPage"));
const TileGroutCleaningParadiseValleyAzPage = lazy(() => import("@/pages/TileGroutCleaningParadiseValleyAzPage"));
const ShowerRestorationGilbertAzPage = lazy(() => import("@/pages/ShowerRestorationGilbertAzPage"));
const StoneRestorationChandlerAzPage = lazy(() => import("@/pages/StoneRestorationChandlerAzPage"));
const StoneRestorationGilbertAzPage = lazy(() => import("@/pages/StoneRestorationGilbertAzPage"));
const StoneRestorationMesaAzPage = lazy(() => import("@/pages/StoneRestorationMesaAzPage"));

const PriceCalculatorPage = lazy(() => import("@/pages/PriceCalculatorPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const AnswersPage = lazy(() => import("@/pages/AnswersPage"));
const BuilderRealtorPage = lazy(() => import("@/pages/BuilderRealtorPage"));
const GroutQuizPage = lazy(() => import("@/pages/GroutQuizPage"));
const FreeAssessmentPage = lazy(() => import("@/pages/FreeAssessmentPage"));
const AccessibilityPage = lazy(() => import("@/pages/AccessibilityPage"));
const SmsTermsPage = lazy(() => import("@/pages/SmsTermsPage"));
const CookiesPage = lazy(() => import("@/pages/CookiesPage"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const HomeownerDashboard = lazy(() => import("@/pages/HomeownerDashboard"));
const OAuthConsent = lazy(() => import("@/pages/OAuthConsent"));


// Real city+service landing page slugs ,  exclude these from the legacy redirect map
const CITY_SERVICE_SLUGS = new Set(cityServiceCombos.map((c) => `/${c.slug}`));

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// ===================== 301 REDIRECT MAP =====================
// All doorway/city pages consolidated to parent service or /service-areas
const redirects: { from: string; to: string }[] = [
  // --- Tile & grout cleaning city pages → /tile-grout-cleaning ---
  { from: "/tile-cleaning-phoenix", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-gilbert", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-chandler", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-mesa", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-scottsdale", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-queen-creek", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-san-tan-valley", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-ahwatukee", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-paradise-valley", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-tempe", to: "/tile-grout-cleaning" },
  { from: "/tile-grout-cleaning-phoenix", to: "/tile-grout-cleaning" },
  { from: "/tile-grout-cleaning-gilbert", to: "/tile-grout-cleaning" },
  { from: "/tile-grout-cleaning-chandler", to: "/tile-grout-cleaning" },
  { from: "/tile-grout-cleaning-mesa", to: "/tile-grout-cleaning" },
  { from: "/tile-grout-cleaning-scottsdale", to: "/tile-grout-cleaning" },
  // Legacy -az suffix versions
  { from: "/tile-cleaning-chandler-az", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-mesa-az", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-gilbert-az", to: "/tile-grout-cleaning" },
  { from: "/tile-cleaning-queen-creek-az", to: "/tile-grout-cleaning" },
  // /tile-grout-cleaning-chandler-az is now a dedicated landing page (see route below), not a redirect.
  // /tile-grout-cleaning-mesa-az is now a dedicated landing page (see route below), not a redirect.
  // /tile-grout-cleaning-gilbert-az is now a dedicated landing page, not a redirect.
  { from: "/tile-grout-cleaning-queen-creek-az", to: "/tile-grout-cleaning" },

  // --- Grout color sealing city pages → /grout-color-sealing ---
  { from: "/grout-color-sealing-phoenix", to: "/grout-color-sealing" },
  { from: "/grout-color-sealing-gilbert", to: "/grout-color-sealing" },
  { from: "/grout-color-sealing-chandler", to: "/grout-color-sealing" },
  { from: "/grout-color-sealing-mesa", to: "/grout-color-sealing" },
  { from: "/grout-color-sealing-scottsdale", to: "/grout-color-sealing" },
  { from: "/grout-sealing-phoenix", to: "/grout-color-sealing" },
  { from: "/grout-sealing-gilbert", to: "/grout-color-sealing" },
  { from: "/grout-sealing-chandler", to: "/grout-color-sealing" },
  { from: "/grout-sealing-mesa", to: "/grout-color-sealing" },
  // /grout-color-sealing-chandler-az is now a dedicated landing page (see route below), not a redirect.
  // /grout-color-sealing-mesa-az is now a dedicated landing page (see route below), not a redirect.

  // --- Travertine / stone restoration city pages → /travertine-restoration ---
  { from: "/stone-restoration-phoenix", to: "/travertine-restoration" },
  { from: "/stone-restoration-gilbert", to: "/travertine-restoration" },
  { from: "/stone-restoration-chandler", to: "/travertine-restoration" },
  { from: "/stone-restoration-mesa", to: "/travertine-restoration" },
  { from: "/stone-restoration-scottsdale", to: "/travertine-restoration" },
  { from: "/travertine-restoration-phoenix", to: "/travertine-restoration" },
  
  { from: "/travertine-restoration-chandler", to: "/travertine-restoration" },
  { from: "/travertine-restoration-mesa", to: "/travertine-restoration" },
  { from: "/travertine-restoration-chandler-az", to: "/service-areas/chandler" },
  { from: "/travertine-restoration-mesa-az", to: "/service-areas/mesa" },
  { from: "/travertine-restoration-gilbert-az", to: "/service-areas/gilbert" },
  { from: "/travertine-restoration-scottsdale-az", to: "/travertine-restoration" },

  // --- Marble polishing city pages → /marble-polishing ---
  { from: "/marble-polishing-phoenix", to: "/marble-polishing" },
  { from: "/marble-polishing-gilbert", to: "/marble-polishing" },
  { from: "/marble-polishing-chandler", to: "/marble-polishing" },
  { from: "/marble-polishing-mesa", to: "/marble-polishing" },
  { from: "/marble-polishing-chandler-az", to: "/service-areas/chandler" },
  { from: "/marble-polishing-scottsdale-az", to: "/marble-polishing" },

  // --- Shower restoration city pages → /services/shower-restoration ---
  { from: "/shower-restoration-phoenix", to: "/services/shower-restoration" },
  { from: "/shower-restoration-gilbert", to: "/services/shower-restoration" },
  { from: "/shower-restoration-chandler", to: "/services/shower-restoration" },
  { from: "/shower-restoration-mesa", to: "/services/shower-restoration" },
  { from: "/shower-cleaning-phoenix", to: "/services/shower-restoration" },
  { from: "/shower-cleaning-gilbert", to: "/services/shower-restoration" },
  { from: "/shower-cleaning-chandler", to: "/services/shower-restoration" },
  { from: "/shower-cleaning-mesa", to: "/services/shower-restoration" },
  { from: "/shower-restoration-chandler-az", to: "/service-areas/chandler" },
  { from: "/shower-restoration-mesa-az", to: "/service-areas/mesa" },

  // --- Countertop restoration city pages → /countertop-restoration ---
  { from: "/countertop-restoration-phoenix", to: "/countertop-restoration" },
  { from: "/countertop-restoration-gilbert", to: "/countertop-restoration" },
  { from: "/countertop-restoration-chandler", to: "/countertop-restoration" },
  { from: "/countertop-restoration-mesa", to: "/countertop-restoration" },

  // --- Carpet cleaning city pages → /services (page removed) ---
  { from: "/carpet-cleaning-phoenix", to: "/services" },
  { from: "/carpet-cleaning-gilbert", to: "/services" },
  { from: "/carpet-cleaning-chandler", to: "/services" },
  { from: "/carpet-cleaning-mesa", to: "/services" },
  { from: "/carpet-cleaning-scottsdale", to: "/services" },
  { from: "/carpet-cleaning-chandler-az", to: "/services" },
  { from: "/carpet-cleaning-gilbert-az", to: "/services" },
  { from: "/carpet-cleaning-paradise-valley-az", to: "/services" },

  // --- Stone sealing city pages → /stone-sealing ---
  { from: "/stone-sealing-chandler-az", to: "/stone-sealing" },
  { from: "/stone-sealing-scottsdale-az", to: "/stone-sealing" },

  // --- Saltillo city pages → /saltillo-tile-restoration ---
  { from: "/saltillo-tile-restoration-phoenix-az", to: "/saltillo-tile-restoration" },

  // --- Epoxy city pages → /services/epoxy ---
  { from: "/epoxy-phoenix", to: "/services/epoxy" },
  { from: "/epoxy-gilbert", to: "/services/epoxy" },
  { from: "/epoxy-chandler", to: "/services/epoxy" },
  { from: "/epoxy-mesa", to: "/services/epoxy" },
  { from: "/epoxy-installation-phoenix", to: "/services/epoxy" },
  { from: "/epoxy-installation-gilbert", to: "/services/epoxy" },
  { from: "/epoxy-installation-chandler", to: "/services/epoxy" },
  { from: "/epoxy-installation-mesa", to: "/services/epoxy" },

  // --- Commercial city pages → /commercial ---
  { from: "/commercial-tile-cleaning-phoenix", to: "/commercial" },
  { from: "/commercial-tile-cleaning-gilbert", to: "/commercial" },
  { from: "/commercial-tile-cleaning-chandler", to: "/commercial" },
  { from: "/commercial-tile-cleaning-mesa", to: "/commercial" },
  { from: "/commercial-grout-restoration-phoenix", to: "/commercial" },
  { from: "/commercial-grout-restoration-gilbert", to: "/commercial" },
  { from: "/commercial-grout-restoration-chandler", to: "/commercial" },
  { from: "/commercial-grout-restoration-mesa", to: "/commercial" },

  // --- General "services in city" and city-only pages → /service-areas ---
  { from: "/services-phoenix", to: "/service-areas" },
  { from: "/services-gilbert", to: "/service-areas" },
  { from: "/services-chandler", to: "/service-areas" },
  { from: "/services-mesa", to: "/service-areas" },
  { from: "/services-scottsdale", to: "/service-areas" },

  // --- SEO city pages (formerly standalone city pages) → /service-areas ---
  { from: "/chandler-az", to: "/service-areas" },
  { from: "/mesa-az", to: "/service-areas" },
  { from: "/gilbert-az", to: "/service-areas" },
  { from: "/queen-creek-az", to: "/service-areas" },
  { from: "/san-tan-valley-az", to: "/service-areas" },
  { from: "/scottsdale-az", to: "/service-areas" },
  { from: "/ahwatukee-az", to: "/service-areas" },
  { from: "/casa-grande-az", to: "/service-areas" },
  { from: "/phoenix-az", to: "/service-areas" },
  { from: "/florence-az", to: "/service-areas" },
  { from: "/paradise-valley-az", to: "/service-areas" },
  { from: "/tempe-az", to: "/service-areas" },
  { from: "/apache-junction-az", to: "/service-areas" },
  { from: "/arcadia-az", to: "/service-areas" },
  { from: "/cave-creek-az", to: "/service-areas" },
  { from: "/biltmore-az", to: "/service-areas" },
  { from: "/camelback-az", to: "/service-areas" },
  { from: "/fountain-hills-az", to: "/service-areas" },
  { from: "/glendale-az", to: "/service-areas" },
  { from: "/coolidge-az", to: "/service-areas" },
  { from: "/carefree-az", to: "/service-areas" },
  { from: "/gold-canyon-az", to: "/service-areas" },
  { from: "/fort-mcdowell-az", to: "/service-areas" },
  { from: "/globe-az", to: "/service-areas" },
  { from: "/rio-verde-az", to: "/service-areas" },
  { from: "/eloy-az", to: "/service-areas" },
  { from: "/maricopa-az", to: "/service-areas" },

  // --- Legacy city slugs without -az suffix → /service-areas ---
  { from: "/queen-creek", to: "/service-areas" },
  { from: "/san-tan-valley", to: "/service-areas" },
  { from: "/chandler", to: "/service-areas" },
  { from: "/gilbert", to: "/service-areas" },
  { from: "/mesa", to: "/service-areas" },
  { from: "/phoenix", to: "/service-areas" },
  { from: "/scottsdale", to: "/service-areas" },
  { from: "/paradise-valley", to: "/service-areas" },
  { from: "/ahwatukee", to: "/service-areas" },
  { from: "/tempe", to: "/service-areas" },

  // --- Grout & stone install city pages → /services ---
  { from: "/grout-stone-install-paradise-valley", to: "/services" },
  { from: "/grout-stone-install-gilbert", to: "/services" },
  { from: "/grout-stone-install-chandler", to: "/services" },
  { from: "/grout-stone-install-ahwatukee", to: "/services" },

  // --- Additional legacy doorway pages → appropriate targets ---
  { from: "/grout-stone-install-gilbert", to: "/services" },
  { from: "/es-mx", to: "/" },
  { from: "/es-mx/about", to: "/about" },
  { from: "/es-mx/contact", to: "/contact" },
  { from: "/es-mx/services", to: "/services" },
  { from: "/permanent-non-slip-nano-coating-stain-resistant-surfaces-await-you", to: "/services/slip-resistant" },
  { from: "/carpet-cleaning-scottsdale-az", to: "/services" },
  { from: "/grout-color-sealing-scottsdale-az", to: "/grout-color-sealing" },
  // /grout-color-sealing-gilbert-az is now a dedicated landing page (see route below), not a redirect.
  { from: "/contact-us0274ce01", to: "/contact" },

  // --- City+service -az doorways → matching /service-areas/{city} (added per SEO consolidation) ---
  { from: "/tile-grout-cleaning-ahwatukee-az", to: "/service-areas/ahwatukee" },
  // /tile-grout-cleaning-paradise-valley-az is now a dedicated landing page (see route below), not a redirect.
  { from: "/grout-color-sealing-ahwatukee-az", to: "/service-areas/ahwatukee" },
  // /grout-color-sealing-paradise-valley-az is now a dedicated landing page (see route below), not a redirect.
  { from: "/travertine-restoration-ahwatukee-az", to: "/service-areas/ahwatukee" },
  { from: "/travertine-restoration-paradise-valley-az", to: "/service-areas/paradise-valley" },
  { from: "/marble-polishing-mesa-az", to: "/service-areas/mesa" },
  { from: "/marble-polishing-gilbert-az", to: "/service-areas/gilbert" },
  { from: "/marble-polishing-ahwatukee-az", to: "/service-areas/ahwatukee" },
  { from: "/marble-polishing-paradise-valley-az", to: "/service-areas/paradise-valley" },
  { from: "/shower-restoration-ahwatukee-az", to: "/service-areas/ahwatukee" },
  { from: "/shower-restoration-paradise-valley-az", to: "/service-areas/paradise-valley" },
  // /stone-restoration-{chandler,gilbert,mesa}-az are now dedicated landing pages (see routes below), not redirects.
  { from: "/stone-restoration-ahwatukee-az", to: "/service-areas/ahwatukee" },
  { from: "/stone-restoration-paradise-valley-az", to: "/service-areas/paradise-valley" },

  // --- Legacy URL redirects ---
  { from: "/natural-stone-and-tile-services", to: "/services" },
  { from: "/portfolio", to: "/testimonials" },
  { from: "/services/countertop-restoration", to: "/countertop-restoration" },
  { from: "/services/tile-restoration", to: "/tile-grout-cleaning" },

  // --- Legacy blog redirects (URL stability - renamed slugs 301 to current) ---
  { from: "/blog/color-sealing-vs-retiling-arizona-2026", to: "/blog/tile-restoration-vs-retiling-cost-comparison-arizona" },
  { from: "/blog/arizona-hard-water-tile-damage", to: "/blog/remove-hard-water-stains-shower-arizona" },
  { from: "/blog/shower-mold-remediation", to: "/blog/shower-mold-dangers" },
  { from: "/blog/gilbert-hard-water-tile-grout-damage", to: "/blog/hard-water-stains-gilbert-shower-tile" },
  { from: "/blog/grout-repair-vs-retiling-cost-comparison-arizona", to: "/blog/tile-restoration-vs-retiling-cost-comparison-arizona" },
  { from: "/blog/marmocer-marble-paradise-valley-restoration", to: "/blog/spot-bonded-marmocer-marble-restoration-paradise-valley" },
  { from: "/blog/shower-restoration-signs-arizona", to: "/blog/shower-restoration-signs" },
  { from: "/blog/tile-grout-cleaning-cost-gilbert-arizona-2026", to: "/tile-grout-cleaning-gilbert-az" },
  { from: "/blog/travertine-sealing-arizona-frequency-guide", to: "/blog/arizona-travertine-needs-professional-sealer" },
  { from: "/blog/vinegar-ruining-tile-floors-arizona", to: "/blog/grout-haze-removal-arizona-why-vinegar-fails" },

  // --- Consolidated page redirects ---
  
  { from: "/architects-residential", to: "/services" },
  { from: "/architects-commercial", to: "/services" },
  { from: "/site-prep", to: "/services" },
  { from: "/tile-and-grout-cleaning", to: "/services/tile-grout-cleaning" },
  { from: "/grout-repair", to: "/grout-color-sealing" },
  { from: "/grout-visualizer", to: "/grout-color-sealing" },
  { from: "/travertine-cleaning-sealing", to: "/travertine-restoration" },
  { from: "/stone-polishing", to: "/services/stone-floor" },

  // --- Legacy /services/* slugs that no longer resolve → correct targets ---
  { from: "/services/epoxy-installation", to: "/services/epoxy" },
  { from: "/services/stone-polishing", to: "/services/stone-floor" },
  { from: "/services/concrete-polishing", to: "/services/epoxy" },
];

export function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<PageLoader />}>
        <Routes location={location} key={location.pathname}>
          {/* ==================== CORE PAGES ==================== */}
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/services" element={<PageTransition><ServicesPage /></PageTransition>} />
          {/* Service-specific redirects (must precede :slug catch-all) */}
          <Route path="/services/grout-restoration" element={<Navigate to="/services/tile-grout-restoration" replace />} />
          <Route path="/services/grout-color-sealing" element={<Navigate to="/grout-color-sealing" replace />} />
          <Route path="/services/grout-repair" element={<Navigate to="/grout-color-sealing" replace />} />
          <Route path="/services/travertine-cleaning-sealing" element={<Navigate to="/travertine-restoration" replace />} />
          <Route path="/services/flooring-installation" element={<Navigate to="/services" replace />} />
          <Route path="/services/shower-installation" element={<Navigate to="/services/shower-restoration" replace />} />
          <Route path="/services/countertop-installation" element={<Navigate to="/countertop-restoration" replace />} />
          <Route path="/services/shower-glass-restoration" element={<Navigate to="/services/shower-restoration" replace />} />
          <Route path="/services/:slug" element={<PageTransition><ServiceDetailPage /></PageTransition>} />
          <Route path="/commercial" element={<PageTransition><CommercialServicesPage /></PageTransition>} />
          <Route path="/commercial/:slug" element={<PageTransition><CommercialServiceDetailPage /></PageTransition>} />
          <Route path="/the-zone" element={<PageTransition><TheZonePage /></PageTransition>} />
          <Route path="/service-areas" element={<PageTransition><ServiceAreasPage /></PageTransition>} />
          <Route path="/gilbert-tile-grout-restoration" element={<PageTransition><GilbertRestorationPage /></PageTransition>} />
          {["las-sendas-mesa-az", "the-pecans-queen-creek-az", "trilogy-at-power-ranch-gilbert-az"].map(neighborhood => (
            <Route key={neighborhood} path={`/service-areas/${neighborhood}`} element={<PageTransition><NeighborhoodPreservationPage neighborhoodSlug={neighborhood} /></PageTransition>} />
          ))}
          {/* City slugs without a dedicated landing page 301 to the hub (must precede :slug) */}
          {["ahwatukee", "paradise-valley", "queen-creek", "scottsdale", "tempe"].map((city) => (
            <Route key={city} path={`/service-areas/${city}`} element={<Navigate to="/service-areas" replace />} />
          ))}
          {/* Dedicated city landing pages ,  must come before any catch-all */}
          <Route path="/service-areas/:slug" element={<PageTransition><CityLandingPage /></PageTransition>} />
          <Route path="/blog" element={<PageTransition><BlogPage /></PageTransition>} />
          <Route path="/blog/archive" element={<PageTransition><BlogArchivePage /></PageTransition>} />
          {/* Blog slug 301 redirects (must precede /blog/:slug catch-all) */}
          {redirects
            .filter(({ from }) => from.startsWith("/blog/"))
            .map(({ from, to }) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
          <Route path="/blog/:slug" element={<PageTransition><BlogPostPage /></PageTransition>} />
          <Route path="/about" element={<PageTransition><AboutPage /></PageTransition>} />
          <Route path="/faq" element={<PageTransition><FAQPage /></PageTransition>} />
          <Route path="/testimonials" element={<PageTransition><TestimonialsPage /></PageTransition>} />
          <Route path="/case-studies" element={<PageTransition><CaseStudiesPage /></PageTransition>} />
          <Route path="/case-studies/:slug" element={<PageTransition><CaseStudyDetailPage /></PageTransition>} />

          
          <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><PrivacyPage /></PageTransition>} />
          <Route path="/terms" element={<PageTransition><TermsPage /></PageTransition>} />
          <Route path="/sms-terms" element={<PageTransition><SmsTermsPage /></PageTransition>} />
          <Route path="/accessibility" element={<PageTransition><AccessibilityPage /></PageTransition>} />
          <Route path="/cookies" element={<PageTransition><CookiesPage /></PageTransition>} />

          <Route path="/warranty" element={<PageTransition><WarrantyPage /></PageTransition>} />
          <Route path="/price-calculator" element={<PageTransition><PriceCalculatorPage /></PageTransition>} />
          <Route path="/pricing" element={<PageTransition><PricingPage /></PageTransition>} />
          <Route path="/answers" element={<PageTransition><AnswersPage /></PageTransition>} />

          <Route path="/builder-realtor" element={<PageTransition><BuilderRealtorPage /></PageTransition>} />
          <Route path="/grout-quiz" element={<PageTransition><GroutQuizPage /></PageTransition>} />
          <Route path="/free-assessment" element={<PageTransition><FreeAssessmentPage /></PageTransition>} />
          <Route path="/promotions" element={<PageTransition><PromotionsPage /></PageTransition>} />

          {/* Legacy blog URL without /blog/ prefix */}
          <Route path="/why-is-my-grout-stained-but-i-mop-all-the-time" element={<BlogRedirect />} />

          {/* ==================== SEO SERVICE PAGES (kept ,  unique content) ==================== */}
          <Route path="/grout-color-sealing" element={<PageTransition><GroutColorSealingPage /></PageTransition>} />
          <Route path="/grout-color-chart" element={<PageTransition><GroutColorChartPage /></PageTransition>} />
          <Route path="/hard-water-stain-removal-arizona" element={<PageTransition><HardWaterStainRemovalPage /></PageTransition>} />
          <Route path="/natural-stone-shower-restoration" element={<PageTransition><NaturalStoneShowerPage /></PageTransition>} />
          <Route path="/tile-stone-repair" element={<PageTransition><TileStoneRepairPage /></PageTransition>} />
          {/* /shower-restoration and /tile-grout-cleaning consolidated to canonical /services/* twins (per SEO dedup) */}
          <Route path="/shower-restoration" element={<Navigate to="/services/shower-restoration" replace />} />
          <Route path="/tile-grout-cleaning" element={<Navigate to="/services/tile-grout-cleaning" replace />} />

          <Route path="/travertine-restoration" element={<PageTransition><SEOServicePage /></PageTransition>} />
          <Route path="/travertine-restoration-gilbert" element={<PageTransition><TravertineGilbertPage /></PageTransition>} />
          <Route path="/grout-color-sealing-gilbert-az" element={<PageTransition><GroutColorSealingGilbertAzPage /></PageTransition>} />
          <Route path="/grout-color-sealing-chandler-az" element={<PageTransition><GroutColorSealingChandlerAzPage /></PageTransition>} />
          <Route path="/grout-color-sealing-mesa-az" element={<PageTransition><GroutColorSealingMesaAzPage /></PageTransition>} />
          <Route path="/grout-color-sealing-paradise-valley-az" element={<PageTransition><GroutColorSealingParadiseValleyAzPage /></PageTransition>} />
          <Route path="/tile-grout-cleaning-chandler-az" element={<PageTransition><TileGroutCleaningChandlerAzPage /></PageTransition>} />
          <Route path="/tile-grout-cleaning-gilbert-az" element={<PageTransition><TileGroutCleaningGilbertAzPage /></PageTransition>} />
          <Route path="/tile-grout-cleaning-mesa-az" element={<PageTransition><TileGroutCleaningMesaAzPage /></PageTransition>} />
          <Route path="/tile-grout-cleaning-paradise-valley-az" element={<PageTransition><TileGroutCleaningParadiseValleyAzPage /></PageTransition>} />
          <Route path="/shower-restoration-gilbert-az" element={<PageTransition><ShowerRestorationGilbertAzPage /></PageTransition>} />
          <Route path="/stone-restoration-chandler-az" element={<PageTransition><StoneRestorationChandlerAzPage /></PageTransition>} />
          <Route path="/stone-restoration-gilbert-az" element={<PageTransition><StoneRestorationGilbertAzPage /></PageTransition>} />
          <Route path="/stone-restoration-mesa-az" element={<PageTransition><StoneRestorationMesaAzPage /></PageTransition>} />

          <Route path="/marble-polishing" element={<PageTransition><SEOServicePage /></PageTransition>} />
          <Route path="/stone-sealing" element={<PageTransition><SEOServicePage /></PageTransition>} />
          <Route path="/countertop-restoration" element={<PageTransition><SEOServicePage /></PageTransition>} />
          <Route path="/saltillo-tile-restoration" element={<PageTransition><SEOServicePage /></PageTransition>} />
          <Route path="/carpet-cleaning" element={<Navigate to="/services" replace />} />
          <Route path="/pool-deck-restoration" element={<PageTransition><SEOServicePage /></PageTransition>} />
          <Route path="/biohazard-remediation" element={<PageTransition><SEOServicePage /></PageTransition>} />
          <Route path="/headstone-restoration" element={<PageTransition><SEOServicePage /></PageTransition>} />

          {/* ==================== CITY + SERVICE LANDING PAGES ==================== */}
          {cityServiceCombos.map((combo) => (
            <Route
              key={combo.slug}
              path={`/${combo.slug}`}
              element={<PageTransition><CityServicePage /></PageTransition>}
            />
          ))}

          {/* ==================== 301 REDIRECTS ==================== */}
          {redirects
            .filter(({ from }) => !CITY_SERVICE_SLUGS.has(from) && !from.startsWith("/blog/"))
            .map(({ from, to }) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}

          {/* 404 */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<HomeownerDashboard />} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          <Route path="/404" element={<PageTransition><NotFound /></PageTransition>} />
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}
